/**
 * Manual-shutter fallback tracker — an escape hatch for eye-gate misfires.
 *
 * The eye-openness gate can, in principle, wrongly block a legitimate,
 * well-framed face (an eye shape, lighting, or demographic we never tested).
 * Because the manual shutter is otherwise enabled only once framing reaches
 * 'ready', such a misfire would leave the user unable to capture AT ALL — the
 * exact hard-stuck failure the eye gate was rebuilt to avoid (the original
 * beard bug). This tracks how many consecutive ticks the face has been
 * well-framed but blocked ONLY by the eye gate, and unlocks the manual shutter
 * after a grace period so the user can force a capture (the server still
 * validates it). Auto-capture is unaffected — it still requires a real 'ready'.
 *
 * Once unlocked it LATCHES for the session (reset() on a new camera session),
 * so the shutter can't flicker off if the eye signal wobbles.
 */
export interface ManualFallbackTracker {
  /** Feed one tick. `blockedOnEyesOnly` = framing is ready but eyes aren't open. */
  update(blockedOnEyesOnly: boolean): boolean;
  /** Clear the streak and the latch (call when the detect loop restarts). */
  reset(): void;
}

export function createManualFallbackTracker(unlockAfterTicks: number): ManualFallbackTracker {
  let stuckTicks = 0;
  let unlocked = false;
  return {
    update(blockedOnEyesOnly: boolean): boolean {
      if (unlocked) return true;
      stuckTicks = blockedOnEyesOnly ? stuckTicks + 1 : 0;
      if (stuckTicks >= unlockAfterTicks) unlocked = true;
      return unlocked;
    },
    reset() {
      stuckTicks = 0;
      unlocked = false;
    },
  };
}
